import { Component, createRef } from 'preact';
import { Portal } from '@/components';
import { SvgIcon } from '@/features/form';
import {
	hasClass,
	encodeQuotes,
	prependChild,
	nodeListToArray,
	anonymizeValue,
	isArray,
	log,
	getNonce
} from '@/utils';
import { screenshotIsRendering } from '@/constants';

let USER_TOUCHING = false;

const dimmerText = createRef();

const findFirstFocusableElement = () => {
	return nodeListToArray(document.getElementsByTagName('*')).filter(isFocusable)[0];
};

const elementVisible = element => {
	if (!element) {
		return false;
	}
	return element.getBoundingClientRect()?.x > 0 && element.getBoundingClientRect()?.y > 0;
};

const isFocusable = element => {
	if (
		element.tabIndex < 0 ||
		element.offsetParent === null ||
		elementVisible(element) === false
	) {
		return false;
	}

	switch (element.tagName) {
		case 'DIV':
			return true;
		case 'A':
			return !!element.href;
		case 'INPUT':
			return element.type !== 'hidden' && !element.disabled;
		case 'SELECT':
		case 'TEXTAREA':
		case 'BUTTON':
			return !element.disabled;
		default:
			return false;
	}
};

export class EventObserver extends Component {
	onMouseMove = e => {
		if (this.props.showCapture) this.props.onMouseMove(e);
	};
	onClick = e => {
		if (this.props.showCapture && !USER_TOUCHING) this.props.onClick(e);
	};
	onKeyDown = e => {
		if (this.props.showCapture && !USER_TOUCHING) this.props.onKeyDown(e);
	};
	onKeyUp = e => {
		e.stopPropagation();
		if (this.props.showCapture && !USER_TOUCHING) this.props.onKeyUp(e);
	};
	onScroll = e => {
		this.props.onScroll(e);
	};
	onTouchEnd = e => {
		USER_TOUCHING = true;
		if (this.props.showCapture && typeof this.props.onClick === 'function')
			this.props.onClick(e);
	};
	componentDidMount() {
		try {
			dimmerText.current.focus();
		} catch (e) {}
		typeof this.props.onMouseMove === 'function' &&
			window.addEventListener('mousemove', this.onMouseMove);
		typeof this.props.onScroll === 'function' &&
			window.addEventListener('scroll', this.onScroll);
		typeof this.props.onClick === 'function' &&
			setTimeout(() => {
				window.addEventListener('mousedown', this.onClick);
				window.addEventListener('click', this.onClick);
			});
		typeof this.props.onKeyDown === 'function' &&
			setTimeout(() => {
				window.addEventListener('keydown', this.onKeyDown);
			});
		typeof this.props.onKeyUp === 'function' &&
			setTimeout(() => {
				window.addEventListener('keyup', this.onKeyUp);
			});
		window.addEventListener('touchend', this.onTouchEnd);
	}
	componentWillUnmount() {
		window.removeEventListener('mousemove', this.onMouseMove);
		window.removeEventListener('scroll', this.onScroll);
		window.removeEventListener('mousedown', this.onClick);
		window.removeEventListener('click', this.onClick);
		window.removeEventListener('touchstart', this.onTouchEnd);
		window.removeEventListener('keydown', this.onKeyDown);
		window.removeEventListener('keyup', this.onKeyUp);
	}
	render({ children }) {
		return children;
	}
}

const initialState = {
	dimTop: {},
	dimRight: {},
	dimLeft: {},
	dimBottom: {},
	dimHighlight: {},
	selectedTarget: false,
	dom: {},
	selector: {}
};
export default class Capture extends Component {
	constructor(props) {
		super(props);
		this.state = initialState;
	}

	mouseMove(e) {
		if (!this.state.selectedTarget && !USER_TOUCHING) {
			this.setDim(e.target);
		}
	}

	componentDidUpdate(prevProps) {
		if (!prevProps.screenshotActive && this.props.screenshotActive) {
			this.setState({
				dimTop: {},
				dimRight: {},
				dimLeft: {},
				dimBottom: {},
				dimHighlight: {},
				selectedTarget: false
			});
		}
	}

	anonymizeDOM(doc) {
		//Check/set base meta head
		const html = doc.querySelector('html');

		nodeListToArray(html.querySelectorAll('script')).forEach(node => {
			try {
				node.parentNode.removeChild(node);
			} catch (e) {}
		});

		const replaceCharactersInAttributesRegex = /(<|>|%3C|%3E|\\u003C|\\u003E)/gi;

		nodeListToArray(html.querySelectorAll('*')).forEach(node => {
			try {
				if (!node.hasAttributes()) {
					return;
				}

				for (const attr of node.attributes) {
					if (replaceCharactersInAttributesRegex.test(attr.value)) {
						attr.value = attr.value.replace(replaceCharactersInAttributesRegex, '');
					}
				}
			} catch (e) {
				log(e);
			}
		});

		const dimmer = html.querySelector('#mopinion_dimmer_overlay');
		if (dimmer) {
			dimmer.parentNode.removeChild(dimmer);
		}

		let maskArray = ['[data-mop-supress]'];
		if (this.props.block.properties.hasOwnProperty('maskedSelectors')) {
			maskArray = [...maskArray, this.props.block.properties.maskedSelectors];
		}

		nodeListToArray(html.querySelectorAll('input, select, textarea')).forEach(el => {
			try {
				anonymizeValue(el);
			} catch (e) {}
			try {
				el.checked = false;
			} catch (e) {}
			try {
				el.setAttribute('data-mopinion-mask-input', '');
			} catch (e) {}
		});
		maskArray.forEach(selector => {
			try {
				nodeListToArray(html.querySelectorAll(selector)).forEach(elementToMask => {
					elementToMask.setAttribute('data-mopinion-mask-extra', '');
				});
			} catch (e) {}
		});

		const base = html.querySelector('base');
		if (!base) {
			const newBase = doc.createElement('base');
			newBase.href = window.location.origin;
			prependChild(doc.querySelector('head'), newBase);
		} else if (base) {
			const newBaseHref =
				base.href.indexOf(window.location.origin) === -1
					? `${window.location.origin}${
							base.href.charAt(0) === '/' ? base.href : '/' + base.href
						}`
					: base.href;
			base.href = newBaseHref;
		}

		return encodeQuotes(
			html.outerHTML.replace(/(\r\n|\n|\r|\u2028|\u2029)/gm, '').trim()
		);
	}

	anonymizeSelector(target) {
		//Check/set base meta head

		const { block } = this.props;
		let maskArray = ['[data-mop-supress]'];
		if (
			block.properties.hasOwnProperty('maskedSelectors') &&
			isArray(block.properties.maskedSelectors)
		) {
			maskArray = [...maskArray, ...block.properties.maskedSelectors];
		}

		try {
			if (['INPUT', 'TEXTAREA', 'SELECT'].indexOf(target.tagName) > -1) {
				anonymizeValue(target);
			}
		} catch (e) {}

		nodeListToArray(target.querySelectorAll('input, select, textarea')).forEach(el => {
			try {
				anonymizeValue(el);
			} catch (e) {}
			try {
				el.checked = false;
			} catch (e) {}
			try {
				el.setAttribute('data-mopinion-mask-input', '');
			} catch (e) {}
		});
		maskArray.forEach(selector => {
			if (typeof selector === 'string') {
				let selectorEl = document.querySelector(selector);
				if (selectorEl && target.isEqualNode(selectorEl)) {
					target.innerHTML = '***';
				} else {
					nodeListToArray(target.querySelectorAll(selector)).forEach(element => {
						element.innerHTML = '***';
					});
				}
			}
		});

		return target.outerHTML.replace(/(\r\n|\n|\r)/gm, ' ').trim();
	}

	onClick(e, fromTouchButton = false) {
		if (!this.props.showCapture) {
			return;
		}

		if (this.props.formType === 'embed' && this.state.selectedTarget) {
			return;
		}

		if (e.type !== 'mousedown') {
			e.preventDefault();
		}
		e.stopPropagation();

		if (
			(!this.state.selectedTarget && !this.dimmer?.contains(e.target)) ||
			USER_TOUCHING ||
			this.props.contentDivRef?.current?.contains(e.selectedTarget)
		) {
			let target = e.target;
			// let temp = document.querySelector('#mopinion_dimmer_overlay');
			// temp.parentNode.removeChild(temp);
			if (USER_TOUCHING && !hasClass(e.target, 'btn-select-touch')) {
				return this.setDim(e.target, true);
			} else if (USER_TOUCHING && hasClass(e.target, 'btn-select-touch')) {
				target = this.state.selectedTarget;
			}

			try {
				document
					.querySelector('[data-mopinion-screen-capture]')
					.removeAttribute('[data-mopinion-screen-capture]');
			} catch (e) {}
			target.setAttribute('data-mopinion-screen-capture', '');
			const selector = this.anonymizeSelector(target.cloneNode(true));
			const documentWithCss = this.applyCollectedCss(document.cloneNode(true));
			const dom = this.anonymizeDOM(documentWithCss);

			return this.setState(
				() => {
					return {
						dom: dom,
						selector: selector
					};
				},
				() => {
					this.setDim(target, true);

					this.props.setScreenshot({
						dom: this.state.dom,
						selector: this.state.selector,
						value: screenshotIsRendering
					});
					this.props.toggleCapture(false);
				}
			);
		}
	}

	applyCollectedCss(clonedDoc) {
		let styles = '';
		for (const sheet of document.styleSheets) {
			try {
				for (const rule of sheet.cssRules) {
					styles += rule.cssText;
				}

				//try to remove included stylesheets to prevent duplicate loading
				const ownerNode = sheet.ownerNode;
				for (const matchingNode of clonedDoc.querySelectorAll(ownerNode.nodeName)) {
					if (ownerNode.isEqualNode(matchingNode)) {
						matchingNode.parentNode.removeChild(matchingNode);
					}
				}
			} catch (e) {
				//external stylesheets will fail -> keep those so we can try to load them in the generation job
				log({
					e,
					sheet
				});
			}
		}

		for (const inlineStyleNode of clonedDoc.querySelectorAll('style')) {
			try {
				inlineStyleNode.parentNode.removeChild(inlineStyleNode);
			} catch (e) {
				log(e);
			}
		}

		const styleNode = clonedDoc.createElement('style');
		const nonce = getNonce('style');
		if (nonce) {
			styleNode.nonce = nonce;
		}
		styleNode.appendChild(document.createTextNode(styles));
		clonedDoc.head.appendChild(styleNode);

		return clonedDoc;
	}

	onKeyDown(e) {
		if (e.key === 'Escape') {
			this.props.toggleCapture(false);
			this.props.setScreenshot({
				dom: '',
				selector: '',
				value: ''
			});
			this.setState(initialState);
		}
		if (['Enter', 'Space'].indexOf(e.code) > -1) {
			e.stopPropagation();
			this.onClick(e);
		}
	}

	onKeyUp(e) {
		if (e.key === 'Tab') {
			this.setDim(e.target);
		}
	}

	onScroll(e) {
		if (this.state.selectedTarget) {
			this.setDim(this.state.selectedTarget, true);
		}
	}

	setDim(target, selected) {
		let position = target.getBoundingClientRect();
		let borderWidth = 3;
		let dimStyle = {
			position: 'fixed',
			background: 'rgba(0,0,0,.3)',
			pointerEvents: 'none'
		};

		this.setState(() => {
			let newState = {
				dimTop: Object.assign({}, dimStyle, {
					top: 0,
					height: position.top,
					left: 0,
					right: 0
				}),
				dimRight: Object.assign({}, dimStyle, {
					top: position.top,
					height: position.height,
					width: window.innerWidth - position.right,
					right: 0
				}),
				dimBottom: Object.assign({}, dimStyle, {
					top: position.bottom,
					bottom: 0,
					left: 0,
					right: 0
				}),
				dimLeft: Object.assign({}, dimStyle, {
					top: position.top,
					height: position.height,
					left: 0,
					width: position.left
				})
			};

			if (target && selected) {
				newState.dimHighlight = {
					position: 'fixed',
					top: position.top - borderWidth,
					left: position.left - borderWidth,
					height: position.height + borderWidth * 2,
					width: position.width + borderWidth * 2
				};
				newState.selectedTarget = target;
			} else {
				newState.dimHighlight = {};
				newState.tempHighlight = {
					position: 'fixed',
					top: position.top - borderWidth,
					left: position.left - borderWidth,
					height: position.height + borderWidth * 2,
					width: position.width + borderWidth * 2
				};

				if (this.props.formType === 'embed') {
					newState.tempHighlight.borderColor = '#B6B6B6';
				}
			}

			return { ...newState };
		});
	}

	render(props, state, context) {
		if (!props.showCapture) {
			return null;
		}
		return (
			<div>
				<Portal into={document.body}>
					<EventObserver
						onMouseMove={e => {
							this.mouseMove(e);
						}}
						onClick={e => {
							this.onClick(e);
						}}
						onKeyDown={e => {
							this.onKeyDown(e);
						}}
						onKeyUp={e => {
							this.onKeyUp(e);
						}}
						onScroll={e => {
							this.onScroll(e);
						}}
						showCapture={props.screenshotActive}
						dimmerRef={this.dimmerNode}
					>
						<div
							style={{ zIndex: 1999999998 }}
							id="mopinion_dimmer_overlay"
							data-dimmer-for={props.formKey}
							class={props.themeClass + ' mopinion-dimmer wcag'}
							ref={node => (this.dimmer = node)}
						>
							<div class="dimmer-border"></div>
							<div class="dimmer-actions">
								{Object.keys(state.dimHighlight).length === 0 && (
									<div
										class="dimmer-text"
										id="dimmer-text"
										tabIndex={0}
										ref={dimmerText}
									>
										<SvgIcon
											key="mouse"
											icon="mouse-pointer"
											style={{ marginRight: '10px' }}
											alt="mouse-pointer icon"
										/>
										{props.text.screenCaptureText}
									</div>
								)}
								{Object.keys(state.dimHighlight).length > 0 && (
									<div>
										<SvgIcon
											key="check"
											icon="check"
											style={{ marginRight: '10px' }}
											alt="check icon"
										/>
										{props.text.screenCaptureSelected}
									</div>
								)}

								<button
									class="dimmer-cancel"
									onClick={() => {
										this.props.toggleCapture(false);
										this.props.setScreenshot({
											dom: '',
											selector: '',
											value: ''
										});
										this.setState(initialState);
									}}
									onKeyDown={e => {
										if (['Enter', 'Space'].indexOf(e.key) > -1) {
											e.stopPropagation();
											this.props.toggleCapture(false);
											this.props.setScreenshot({
												dom: '',
												selector: '',
												value: ''
											});
											this.setState(initialState);
										}
										if (e.key === 'Tab' && !e.shiftKey) {
											e.preventDefault();
											document.activeElement.blur();
											findFirstFocusableElement()?.focus();
										}
									}}
									aria-label="Cancel"
								>
									&times;
								</button>
							</div>
							{Object.keys(state.dimTop).length > 0 && (
								<div>
									{!props.hideBackdrop && (
										<div>
											<div style={state.dimTop}></div>
											<div style={state.dimRight}></div>
											<div style={state.dimBottom}></div>
											<div style={state.dimLeft}></div>
										</div>
									)}
									{Object.keys(state.dimHighlight).length ? (
										<div
											class={`dimmer-highlight dimmer-selected`}
											style={state.dimHighlight}
											onClick={e => {
												e.preventDefault();
												e.stopPropagation();
											}}
										>
											{USER_TOUCHING ? (
												<button
													type="button"
													class="btn-select-touch"
												>
													{props.text.screenCaptureTouchBtnText}
												</button>
											) : null}
										</div>
									) : Object.keys(state.tempHighlight).length > 0 ? (
										<div
											class={`dimmer-highlight`}
											style={state.tempHighlight}
											onClick={e => {
												e.preventDefault();
												e.stopPropagation();
											}}
										></div>
									) : null}
								</div>
							)}
							{Object.keys(state.dimTop).length === 0 && (
								<div>
									<div
										style={{
											position: 'fixed',
											top: 0,
											right: 0,
											bottom: 0,
											left: 0,
											background: 'rgba(0,0,0,.3)'
										}}
									></div>
									<div class="capture-hint">
										{!USER_TOUCHING ? (
											<SvgIcon
												icon="mouse-pointer"
												alt="mouse pointer icon"
											/>
										) : (
											<SvgIcon
												icon="hand-pointer-o"
												alt="hand pointer icon"
											/>
										)}
										<div>{props.text.screenCaptureHint}</div>
									</div>
								</div>
							)}
						</div>
					</EventObserver>
				</Portal>
			</div>
		);
	}
}
